import { useEffect, useRef, useCallback } from 'react';
import { authService } from '@/services/authService';
import { isTokenExpired, willTokenExpireWithin } from '@/utils/jwtUtils';

interface UseTokenMonitorOptions {
  /**
   * How often to check token expiration (in milliseconds)
   * Default: 60 seconds
   */
  checkInterval?: number;
  /**
   * Time window before expiration to refresh token (in milliseconds)
   * Default: 5 minutes
   */
  refreshWindow?: number;
  /**
   * Whether monitoring is enabled
   */
  enabled?: boolean;
}

/**
 * Hook to monitor JWT token expiration and automatically refresh or logout
 * Only works while user is active (listens to app-activity events)
 */
export const useTokenMonitor = ({
  checkInterval = 60 * 1000, // Check every minute
  refreshWindow = 5 * 60 * 1000, // Refresh 5 minutes before expiration
  enabled = true,
}: UseTokenMonitorOptions = {}) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);
  const isRefreshingRef = useRef(false);

  /**
   * Check token expiration and handle accordingly
   */
  const checkToken = useCallback(async () => {
    // Don't check if not active or already refreshing
    if (!isActiveRef.current || isRefreshingRef.current || !enabled) {
      return;
    }

    // Don't check on public routes (login, forgot-password)
    const publicPaths = ['/login', '/forgot-password'];
    if (publicPaths.includes(window.location.pathname)) {
      return;
    }

    try {
      const token = await authService.getToken();
      
      // No token means already logged out
      if (!token) {
        return;
      }

      // Check if access token is expired
      if (isTokenExpired(token)) {
        console.log('[TOKEN_MONITOR] Access token expired, attempting refresh');

        // Try to refresh the token. apiClient.refreshToken() already decides whether a
        // failure warrants logging the user out (invalid/expired refresh token) or is just
        // a transient network/server error worth retrying on the next interval — don't
        // second-guess that decision here.
        isRefreshingRef.current = true;
        try {
          const refreshed = await authService.performTokenRefresh();
          console.log(refreshed ? '[TOKEN_MONITOR] Token refreshed successfully' : '[TOKEN_MONITOR] Token refresh did not succeed this cycle');
        } catch (error) {
          console.error('[TOKEN_MONITOR] Error during token refresh:', error);
        } finally {
          isRefreshingRef.current = false;
        }
      }
      // Check if token will expire soon and user is active - refresh proactively
      else if (willTokenExpireWithin(token, refreshWindow)) {
        console.log('[TOKEN_MONITOR] Token expiring soon, refreshing proactively');

        isRefreshingRef.current = true;
        try {
          const refreshed = await authService.performTokenRefresh();
          console.log(refreshed ? '[TOKEN_MONITOR] Token refreshed successfully' : '[TOKEN_MONITOR] Token refresh did not succeed this cycle');
        } catch (error) {
          console.error('[TOKEN_MONITOR] Error during token refresh:', error);
        } finally {
          isRefreshingRef.current = false;
        }
      }
    } catch (error) {
      // A transient error while reading/decoding the token locally shouldn't force a
      // logout — only a genuine auth rejection from the refresh endpoint should (handled
      // inside apiClient.refreshToken()).
      console.error('[TOKEN_MONITOR] Error checking token:', error);
      isRefreshingRef.current = false;
    }
  }, [enabled, refreshWindow]);

  /**
   * Track user activity
   */
  const trackActivity = useCallback(() => {
    isActiveRef.current = true;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Listen for app activity events
    window.addEventListener('app-activity', trackActivity);
    
    // Also track common user activity events to ensure we know when user is active
    const events = ['mousedown', 'keypress', 'touchstart', 'click', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, trackActivity, { passive: true });
    });

    // Start checking token periodically
    intervalRef.current = setInterval(checkToken, checkInterval);
    
    // Do an initial check
    checkToken();

    return () => {
      window.removeEventListener('app-activity', trackActivity);
      events.forEach(event => {
        document.removeEventListener(event, trackActivity);
      });
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, checkInterval, checkToken, trackActivity]);
};

